import { FinanceOverviewItem } from "../../types/FinanceOverviewItem";
import { ProjectCheck } from "../../types/ProjectCheck";
import ClockodoLogo from "../ClockodoLogo/ClockodoLogo";
import logo from "../../assets/crmlogo.png";
import "./ProjectCard.css";
import ProjectInfoComponent from "../ProjectInfoComponent/ProjectInfoComponent";
import { useEffect, useState } from "react";

import { MessageCircle, CircleDollarSign } from "lucide-react";
import { useSearchParams } from "react-router-dom";

type ManagerProps = {
  variant: "manager";
  projectCheck: ProjectCheck;
  onManagerChange: (id: number, value: string | boolean, type?: "checked") => void;
};

type SupervisorProps = {
  variant: "supervisor";
  projectCheck: ProjectCheck;
  onSupervisorChange: (id: number, value: string | boolean, type?: "checked") => void;
};

type FinanceProps = {
  variant: "finance";
  projectCheck: FinanceOverviewItem;
};

type ProjectCardProps = ManagerProps | SupervisorProps | FinanceProps;

function ProjectCard(props: ProjectCardProps) {
  const [searchParams] = useSearchParams();
  const { projectCheck, variant } = props;
  const isSupervisor = variant === "supervisor";
  const isFinance = variant === "finance";
  const hasBudget = projectCheck.budget !== undefined && projectCheck.budget > 0;

  const hasClockodoNote =
    projectCheck.clockodoNote !== undefined && projectCheck.clockodoNote.trim() !== "";

  const [openInfo, setOpenInfo] = useState(false);
  const toggleInfo = () => setOpenInfo((prev) => !prev);

  const closeInfo = () => {
    window.history.replaceState(null, "", window.location.pathname);
    setOpenInfo(false);
  };

  // check search params
  useEffect(() => {
    const projectId = searchParams.get("projectId");
    if (!projectId || projectId === "") return;

    if (projectCheck.crmProjectId === projectId) setOpenInfo(true);
  }, [searchParams, projectCheck.crmProjectId]);

  return (
    <>
      {openInfo && <ProjectInfoComponent projectCheck={projectCheck} closeInfo={closeInfo} />}
      <div className={`project-card ${variant}`}>
        <div className="project-info">
          <div>
            {projectCheck.projectNumber}
            {variant === "finance" && <> - {projectCheck.managerName}</>}
          </div>

          <div>{projectCheck.clientName}</div>
          <div className="project-title" onClick={() => toggleInfo()}>
            {projectCheck.projectTitle}
          </div>
        </div>

        <div className={`icons-container ${variant}`}>
          <div className={`icons-container-left`}>
            {hasClockodoNote && (
              <MessageCircle
                size={35}
                className="clockodo-note-icon"
                onClick={() => toggleInfo()}
              />
            )}
            {hasBudget && (
              <CircleDollarSign
                size={35}
                className="budget-note-icon"
                onClick={() => toggleInfo()}
              />
            )}
          </div>
          <div className={`icons-container-right`}>
            <div className={`clockodo-logo-container`}>
              <ClockodoLogo clockodoId={projectCheck.clockodoId} />
            </div>

            <div className={"crm-logo-container"}>
              <a
                href={`https://ioz365.crm4.dynamics.com/main.aspx?appid=04584bc9-d9f6-e811-a957-000d3ab3f13b&pagetype=entityrecord&etn=ioz_customerproject&id=${projectCheck.crmProjectId}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <img src={logo} alt="CRM Logo" className="crm-logo" />
              </a>
            </div>
          </div>
        </div>

        {!isFinance && (
          <>
            <div className={`remark`}>
              <label>Bemerkung von PL:</label>
              <input
                title={projectCheck.managerComment}
                value={projectCheck.managerComment}
                disabled={isSupervisor}
                readOnly={isSupervisor}
                onChange={(e) => {
                  if (variant === "manager") {
                    props.onManagerChange(projectCheck.id, e.target.value);
                  }
                }}
              />
            </div>

            <div className={`check`}>
              <input
                type="checkbox"
                checked={projectCheck.checkedByManager}
                disabled={isSupervisor}
                readOnly={isSupervisor}
                onChange={(e) => {
                  if (variant === "manager") {
                    props.onManagerChange(projectCheck.id, e.target.checked, "checked");
                  }
                }}
              />
            </div>
          </>
        )}

        {variant === "supervisor" && (
          <>
            <div className="remark">
              <label>Bemerkung von VG:</label>
              <input
                value={projectCheck.supervisorComment ?? ""}
                onChange={(e) => props.onSupervisorChange(projectCheck.id, e.target.value)}
              />
            </div>

            <div className="check">
              <input
                type="checkbox"
                checked={projectCheck.checkedBySupervisor ?? false}
                onChange={(e) =>
                  props.onSupervisorChange(projectCheck.id, e.target.checked, "checked")
                }
              />
            </div>
          </>
        )}

        {isFinance && (
          <>
            <div className="check-and-comments-container">
              <div className="check-and-comments">
                <div className="check-status">
                  {projectCheck.checkedByManager ? (
                    <label className="checked">PL: OK</label>
                  ) : (
                    <label className="not-checked">PL: NOK</label>
                  )}
                </div>
                <textarea
                  className="finance-comment"
                  title={projectCheck.managerComment}
                  value={projectCheck.managerComment}
                  disabled
                />
              </div>
              <div className="check-and-comments">
                <div className="check-status">
                  {projectCheck.checkedBySupervisor ? (
                    <label className="checked">VG: OK</label>
                  ) : (
                    <label className="not-checked">VG: NOK</label>
                  )}
                </div>
                <textarea
                  className="finance-comment"
                  title={projectCheck.supervisorComment}
                  value={projectCheck.supervisorComment}
                  disabled
                />
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}

export default ProjectCard;
